import React, { useState } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Flex,
  Text,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Icon,
  HStack,
  VStack,
} from '@chakra-ui/react';
import { FiPackage, FiTruck, FiAlertTriangle, FiDownload } from 'react-icons/fi';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import api from '../utils/api';
import Card from '../components/Card/Card';
import CardHeader from '../components/Card/CardHeader';
import CardBody from '../components/Card/CardBody';

const DigikalaOrdersRequiringStock = () => {
  const { id } = useParams();
  const toast = useToast();
  const [isExporting, setIsExporting] = useState(false);

  const {
    data: ordersData,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ['digikala-orders-requiring-stock', id],
    async () => {
      const response = await api.get(`/gateways/${id}/digikala-orders/requiring-stock`);
      return response.data;
    },
    {
      refetchInterval: 300000, // هر 5 دقیقه
      retry: 2,
    }
  );

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const response = await api.get(`/gateways/${id}/digikala-orders/requiring-stock?export_excel=true`);
      toast({
        title: 'صادرات Excel',
        description: response.data.message || 'فایل Excel به ایمیل شما ارسال شد',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'خطا در صادرات',
        description: error.response?.data?.message || 'خطا در ارسال فایل Excel',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <Spinner size="xl" color="blue.500" />
        <Text mr={4}>در حال بارگذاری سفارشات...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertTitle>خطا در دریافت اطلاعات!</AlertTitle>
        <AlertDescription>
          {error.response?.data?.message || 'خطا در دریافت سفارشات نیازمند تأمین'}
        </AlertDescription>
      </Alert>
    );
  }

  const { items = [], stats = {} } = ordersData?.data || {};

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* هشدار مهم */}
        <Card bg="orange.50" borderLeft="4px solid" borderLeftColor="orange.400">
          <CardBody>
            <HStack>
              <Icon as={FiAlertTriangle} color="orange.500" boxSize={5} />
              <VStack align="start" spacing={1}>
                <Text fontWeight="bold" color="orange.800">
                  سفارشات نیازمند تأمین از فروشنده
                </Text>
                <Text fontSize="sm" color="orange.700">
                  این سفارشات به دلیل کمبود موجودی در انبار دیجی‌کالا، نیاز به تأمین از انبار شما دارند
                </Text>
              </VStack>
            </HStack>
          </CardBody>
        </Card>

        {/* آمار کلی */}
        <Card>
          <CardHeader>
            <Flex justify="space-between" align="center">
              <Heading size="md">آمار کلی سفارشات نیازمند تأمین</Heading>
              <Button
                leftIcon={<FiDownload />}
                colorScheme="green"
                size="sm"
                onClick={handleExportExcel}
                isLoading={isExporting}
                loadingText="در حال ارسال..."
              >
                دریافت فایل Excel
              </Button>
            </Flex>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
              <Stat>
                <StatLabel>تعداد سفارشات</StatLabel>
                <StatNumber color="red.500">{stats.totalOrdersFormatted}</StatNumber>
                <StatHelpText>
                  <Icon as={FiPackage} mr={1} />
                  نیازمند تأمین
                </StatHelpText>
              </Stat>

              <Stat>
                <StatLabel>تعداد کل اقلام</StatLabel>
                <StatNumber color="orange.500">{stats.totalQuantityFormatted}</StatNumber>
                <StatHelpText>
                  <Icon as={FiTruck} mr={1} />
                  باید تأمین شود
                </StatHelpText>
              </Stat>

              <Stat>
                <StatLabel>ارزش کل</StatLabel>
                <StatNumber color="blue.500">{stats.totalAmountFormatted}</StatNumber>
                <StatHelpText>ارزش سفارشات</StatHelpText>
              </Stat>

              <Stat>
                <StatLabel>کمبود موجودی</StatLabel>
                <StatNumber color="red.600">{stats.totalStockShortageFormatted}</StatNumber>
                <StatHelpText>
                  <Icon as={FiAlertTriangle} mr={1} />
                  کمبود در انبار دیجی‌کالا
                </StatHelpText>
              </Stat>
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* جدول سفارشات */}
        <Card>
          <CardHeader>
            <Flex justify="space-between" align="center">
              <Heading size="md">لیست سفارشات ({items.length} مورد)</Heading>
              <Button size="sm" onClick={refetch}>
                بروزرسانی
              </Button>
            </Flex>
          </CardHeader>
          <CardBody>
            {items.length === 0 ? (
              <Alert status="info">
                <AlertIcon />
                <AlertTitle>بدون سفارش نیازمند تأمین!</AlertTitle>
                <AlertDescription>
                  در حال حاضر سفارشی که نیاز به تأمین از انبار شما داشته باشد وجود ندارد.
                </AlertDescription>
              </Alert>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>شماره سفارش</Th>
                      <Th>نام محصول</Th>
                      <Th>کد تأمین‌کننده</Th>
                      <Th>تعداد سفارش</Th>
                      <Th>قیمت فروش</Th>
                      <Th>موجودی انبار DK</Th>
                      <Th>موجودی فروشنده</Th>
                      <Th>کمبود</Th>
                      <Th>تاریخ ایجاد</Th>
                      <Th>تاریخ تعهد</Th>
                      <Th>وضعیت</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {items.map((item) => (
                      <Tr key={item.id}>
                        <Td fontWeight="bold" color="blue.600">
                          {item.orderId}
                        </Td>
                        <Td maxW="200px" isTruncated title={item.productTitle}>
                          {item.productTitle}
                        </Td>
                        <Td fontFamily="mono" fontSize="sm">
                          {item.supplierCode}
                        </Td>
                        <Td>
                          <Badge colorScheme="blue" variant="solid">
                            {item.count} عدد
                          </Badge>
                        </Td>
                        <Td fontWeight="bold" color="green.600">
                          {item.sellingPriceFormatted}
                        </Td>
                        <Td>
                          <Badge 
                            colorScheme={item.warehouseStock > 0 ? "green" : "red"} 
                            variant="outline"
                          >
                            {item.warehouseStock} عدد
                          </Badge>
                        </Td>
                        <Td>
                          <Badge 
                            colorScheme={item.sellerStock >= item.count ? "green" : "orange"} 
                            variant="solid"
                          >
                            {item.sellerStock} عدد
                          </Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme="red" variant="solid">
                            {item.stockShortage} عدد
                          </Badge>
                        </Td>
                        <Td fontSize="sm">{item.orderCreatedAtFormatted}</Td>
                        <Td fontSize="sm">{item.commitmentDateFormatted}</Td>
                        <Td>
                          <Badge colorScheme="orange" variant="solid">
                            نیازمند تأمین
                          </Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </CardBody>
        </Card>

        {/* راهنمای استفاده */}
        <Card bg="blue.50" borderLeft="4px solid" borderLeftColor="blue.400">
          <CardBody>
            <VStack align="start" spacing={3}>
              <Text fontWeight="bold" color="blue.800">
                راهنمای استفاده:
              </Text>
              <VStack align="start" spacing={1} fontSize="sm" color="blue.700">
                <Text>• سفارشاتی که در این لیست نمایش داده می‌شوند، نیاز به تأمین از انبار شما دارند</Text>
                <Text>• ستون "کمبود" نشان‌دهنده تعداد اقلامی است که در انبار دیجی‌کالا موجود نیست</Text>
                <Text>• برای دریافت گزارش کامل، از دکمه "دریافت فایل Excel" استفاده کنید</Text>
                <Text>• این لیست هر 5 دقیقه به‌روزرسانی می‌شود</Text>
              </VStack>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Box>
  );
};

export default DigikalaOrdersRequiringStock;